import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import { api, ApiError, unwrap } from '../../api/client';
import { errorMessage, useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import { useAttachments, type RecordType } from '../attachments/api';
import { fileSize } from '../attachments/size';
import { contactKeys } from '../contacts/model';
import { RichTextEditor } from '../../components/RichTextEditor';
import { htmlText } from '../../lib/richText';
import { fillTemplate } from '../whatsapp/messages';
import { fillEmailBody } from './fill';
import { usePdf } from '../whatsapp/pdf';
import type { WhatsappTarget } from '../whatsapp/WhatsappSend';
import { useEmail, useEmailMessages, useSendEmail, type EmailDocumentType } from './api';
import { parseAddresses } from './addresses';

/** The same document description the WhatsApp button takes. */
export type EmailTarget = WhatsappTarget;

/** Where each kind of document keeps its attached files (a ledger: on the contact). */
const RECORD_TYPES: Record<EmailDocumentType, RecordType> = {
  quotation: 'QUOTATION',
  sales_order: 'SALES_ORDER',
  delivery: 'DELIVERY',
  sales_invoice: 'SALES_INVOICE',
  pos_order: 'POS_ORDER',
  purchase_order: 'PURCHASE_ORDER',
  goods_receipt: 'GOODS_RECEIPT',
  supplier_invoice: 'SUPPLIER_BILL',
  payment: 'PAYMENT',
  sales_return: 'SALES_RETURN',
  credit_note: 'CREDIT_NOTE',
  purchase_return: 'PURCHASE_RETURN',
  debit_note: 'DEBIT_NOTE',
  contact_ledger: 'CONTACT',
};

/**
 * The Email button of a document, and its compose dialog: to (the contact's email, else the last
 * address used for this document), cc, subject, message, the document as a PDF and the files
 * attached to it. Shown only when the shop gave this kind of document to one of its mail servers
 * and the user may send.
 */
export function EmailSend({
  target,
  variant = 'button',
  label = 'Email',
  fullWidth,
  defaultOpen = false,
}: {
  target: EmailTarget;
  variant?: 'button' | 'icon';
  label?: string;
  fullWidth?: boolean;
  defaultOpen?: boolean;
}) {
  const email = useEmail();
  const [open, setOpen] = useState(defaultOpen);
  const sender = email.sender(target.documentType);
  if (!email.canSend || !sender) return null;
  const button =
    variant === 'icon' ? (
      <Tooltip title="Send by email">
        <IconButton aria-label="Send by email" color="primary" onClick={() => setOpen(true)}>
          <MailOutlineRounded />
        </IconButton>
      </Tooltip>
    ) : (
      <Button
        variant="outlined"
        startIcon={<MailOutlineRounded />}
        onClick={() => setOpen(true)}
        fullWidth={fullWidth}
      >
        {label}
      </Button>
    );
  return (
    <>
      {button}
      {open &&
        (email.expired ? (
          <ExpiredDialog onClose={() => setOpen(false)} />
        ) : (
          <SendDialog
            target={target}
            template={email.template(target.documentType)}
            from={sender.fromAddress}
            onClose={() => setOpen(false)}
          />
        ))}
    </>
  );
}

function ExpiredDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Email subscription ended</DialogTitle>
      <DialogContent>
        <Typography>
          Email subscription ended, grace used. Contact PackBytes to renew it.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button component={RouterLink} to="/support?message=Hi%2C%20I%20want%20to%20renew%20Email">
          Contact support
        </Button>
        <Button variant="contained" onClick={onClose}>
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SendDialog({
  target,
  template,
  from,
  onClose,
}: {
  target: EmailTarget;
  template: { subject: string; body: string };
  from: string;
  onClose: () => void;
}) {
  const notify = useNotify();
  const send = useSendEmail();
  const pdf = usePdf();
  const { settings } = useShop();
  const contactId = target.contact?.id ?? '';
  // Documents carry only the contact's id, name and phone: look the contact up for its email.
  const contact = useQuery({
    queryKey: contactKeys.detail(contactId),
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id: contactId } } })),
    enabled: !!contactId,
  });
  const walkIn = !target.contact || (target.contact.isWalkIn ?? contact.data?.isWalkIn ?? false);
  const history = useEmailMessages({
    documentType: target.documentType,
    documentId: target.documentId,
  });
  const recordType = RECORD_TYPES[target.documentType];
  const recordId = recordType === 'CONTACT' ? contactId || undefined : target.documentId;
  const files = useAttachments(recordType, recordId);
  const vars = { shop: settings?.name, ...target.vars };
  const [to, setTo] = useState<string | null>(null);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState(() => fillTemplate(template.subject, vars));
  const [body, setBody] = useState(() => fillEmailBody(template.body, vars));
  const [attach, setAttach] = useState(!!target.pdf);
  const [picked, setPicked] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const id = useRef(crypto.randomUUID());
  // Typed address, else the contact's, else the last one used for this document.
  const toValue =
    to ?? ((walkIn ? null : contact.data?.email) || history.data?.data[0]?.to.join(', ') || '');

  const submit = async () => {
    setError(null);
    const toList = parseAddresses(toValue);
    const ccList = parseAddresses(cc);
    if (toList.list.length === 0) {
      setError('Enter the email address.');
      return;
    }
    if (!htmlText(body)) {
      setError('Write the message.');
      return;
    }
    const bad = toList.bad ?? ccList.bad;
    if (bad) {
      setError(`“${bad}” is not an email address.`);
      return;
    }
    setBusy(true);
    try {
      const attachment =
        attach && target.pdf
          ? {
              fileName: target.pdf.fileName,
              contentBase64: await pdf.make(target.pdf.paper, target.pdf.render()),
            }
          : undefined;
      const recipientName = walkIn ? undefined : target.contact?.name;
      await send.mutateAsync({
        id: id.current,
        to: toList.list,
        cc: ccList.list,
        subject: subject.trim(),
        body: body.trim(),
        documentType: target.documentType,
        documentId: target.documentId,
        attachmentIds: picked,
        ...(target.documentRef ? { documentRef: target.documentRef } : {}),
        ...(!walkIn && contactId ? { contactId } : {}),
        ...(recipientName ? { recipientName } : {}),
        ...(attachment ? { attachment } : {}),
      });
      notify.success('Email sent');
      onClose();
    } catch (e) {
      // A new id for the next try (a failed send is logged under this one).
      id.current = crypto.randomUUID();
      setError(e instanceof ApiError ? e.message : errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const records = files.data?.data ?? [];
  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <MailOutlineRounded color="primary" />
        Send by email
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            From {from}
            {!walkIn && target.contact ? ` to ${target.contact.name}` : ''}
          </Typography>
          <TextField
            label="To"
            value={toValue}
            onChange={(e) => setTo(e.target.value)}
            placeholder="name@example.com"
            helperText="Separate more than one address with a comma."
            slotProps={{ htmlInput: { inputMode: 'email', autoComplete: 'email' } }}
            required
            autoFocus={!toValue}
          />
          <TextField
            label="Cc (optional)"
            value={cc}
            onChange={(e) => setCc(e.target.value)}
            slotProps={{ htmlInput: { inputMode: 'email' } }}
          />
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            slotProps={{ htmlInput: { maxLength: 300 } }}
            required
          />
          <RichTextEditor label="Message" value={body} onChange={setBody} />
          {target.pdf && (
            <FormControlLabel
              control={<Checkbox checked={attach} onChange={(e) => setAttach(e.target.checked)} />}
              label={`Attach ${target.pdf.fileName}`}
            />
          )}
          {records.length > 0 && (
            <Stack>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Files on this {recordType === 'CONTACT' ? 'contact' : 'document'}
              </Typography>
              {records.slice(0, 50).map((f) => (
                <FormControlLabel
                  key={f.id}
                  control={
                    <Checkbox
                      checked={picked.includes(f.id)}
                      disabled={!picked.includes(f.id) && picked.length >= 10}
                      onChange={(e) =>
                        setPicked((p) =>
                          e.target.checked ? [...p, f.id] : p.filter((x) => x !== f.id),
                        )
                      }
                    />
                  }
                  label={`${f.fileName} (${fileSize(f.sizeBytes)})`}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<MailOutlineRounded />}
          disabled={busy || !subject.trim()}
          onClick={() => void submit()}
        >
          {busy ? (pdf.making ? 'Making the PDF…' : 'Sending…') : 'Send'}
        </Button>
      </DialogActions>
      {pdf.element}
    </Dialog>
  );
}
